import React from 'react';
import { useParams } from 'react-router-dom';
import ApiController from '../controllers/api';
import CustomBackdrop from './CustomBackdrop';
import Swal from 'sweetalert2';
import { usePermissions } from '../hooks/usePermission';
import noImage from '../assets/svg/404.svg';
import { Box, Typography, Divider, Grid, Card, CardContent, Avatar, Tooltip, Skeleton, Zoom, Button } from '@mui/material';
import { Inventory2, AttachMoney, BrandingWatermark, Memory, QrCode,
  Build, Receipt, CalendarToday, Policy, Tag, Person, Description,
  LocationOn, Business, Category, CheckCircle, Cancel, Replay, SimCardAlert} from '@mui/icons-material';
import imgInfo from '../assets/gif/info.gif';

export default function ShowInfoInventory() {
  const { uuid } = useParams();
  const [openBackdrop, setOpenBackdrop] = React.useState(false);
  const [info, setInfo] = React.useState(null);
  const { isAuthenticated } = usePermissions();
  const apiInstance = React.useMemo(() => new ApiController(process.env.REACT_APP_API_URL), []);

  const getInfo = async (uuid) => await apiInstance.get(`/inventory/info/${uuid}`);

  const stateAsign = (isAsign) => {
    const isAsignBool = Boolean(Number(isAsign));
    const backgroundColor = isAsignBool ? "#d4edda" : "#d9d7f8ff";
    const borderColor = isAsignBool ? "#28a745" : "#3540dcff";
    const textColor = isAsignBool ? "#155724" : "#1c1f72ff";
    return (
        <Tooltip 
          title={isAsignBool 
            ? 'ESTE BIEN YA SE ENCUENTRA DE UN VALE DE RESGUARDO' 
            : 'ESTE BIEN AUN NO ESTA DENTRO DE UN VALE DE RESGUARDO'
          }
          placement="bottom-start"
        >
          <div 
            style={{ 
              display: "inline-flex",
              justifyContent: "center",   
              alignItems: "center",
              padding:"2px 20px",
              borderRadius: "12px",
              border: `1px solid ${borderColor}`,
              background: backgroundColor,
              color: textColor,
              lineHeight: "16px",
              cursor:"help"
            }}
          >
            {isAsignBool ? 'EN RESGUARDO' : 'SIN RESGUARDO'}
          </div>
        </Tooltip>
      );
    }


  const getData = async () => {
      setInfo(null);
      setOpenBackdrop(true);

      const res = await getInfo(uuid);

      if (res?.success && res?.data) setInfo(res.data);

      setOpenBackdrop(false);
  };

  React.useEffect(() => {
    if(!isAuthenticated)return;
    getData();
  }, [uuid]);

  if (openBackdrop) return <CustomBackdrop open />;

  if (!info) {
    return (
      <>
        <CustomBackdrop open={openBackdrop} />
        <Zoom
          in
          timeout={600}
          style={{ marginTop: '100px', marginBottom: '30px' }}
        >
          <Card
            elevation={12}
            sx={{
              maxWidth: 800,
              mx: 'auto',
              borderRadius: 4,
              overflow: 'hidden',
              background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              textAlign: 'center'
            }}
          >
            <Box
              sx={{
                background:
                  'linear-gradient(180deg, #833c3cff 20%, #3b1e1eff 100%)',
                color: 'white',
                p: 4,
                position: 'relative'
              }}
            >
              <SimCardAlert sx={{ fontSize: 140, opacity: 0.15 }} />
              <Typography variant="h6" fontWeight="bold">
                INFORMACIÓN NO DISPONIBLE
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                BIEN NO ENCONTRADO
              </Typography>
            </Box>

            <CardContent sx={{ p: 4 }}>
              <Box
                component="img"
                src={noImage}
                alt="Sin información"
                sx={{
                  width: '100%',
                  maxWidth: 280,
                  mx: 'auto',
                  mb: 3
                }}
              />

              <Typography variant="h6" fontWeight="bold" gutterBottom>
                NO SE PUDO MOSTRAR LA INFORMACIÓN DEL BIEN
              </Typography>

              <Typography variant="body2" color="text.secondary" mb={4}>
                EL BIEN SOLICITADO NO EXISTE, FUE ELIMINADO O NO SE CUENTA CON INFORMACIÓN REGISTRADA DENTRO DEL SISTEMA.
              </Typography>

              <Button
                variant="contained"
                onClick={getData}
                startIcon={<Replay />}
                sx={{
                  background:
                    'linear-gradient(180deg, #833c3cff 20%, #3b1e1eff 100%)',
                  px: 4,
                  borderRadius: 2,
                  '&:hover': { opacity: 0.9 }
                }}
              >
                REINTENTAR
              </Button>
            </CardContent>
          </Card>
        </Zoom>
      </>
    );
  };

  return (
    <>
      <CustomBackdrop open={openBackdrop} />
      <Zoom in timeout={800} style={{marginTop:'100px', marginBottom:'30px'}}>
        <Card
          elevation={12}
          sx={{
            maxWidth: 900,
            mx: 'auto',
            borderRadius: 4,
            overflow: 'hidden',
            background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            border: '1px solid rgba(59, 130, 246, 0.2)'
          }}
        >

          {/* HEADER CON IMAGEN Y CÓDIGO */}
          <Box
            sx={{
              background: 'linear-gradient(180deg, #833c3cff 20%, #3b1e1eff 100%)',
              color: 'white',
              p: 4,
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ position: 'absolute', top: 0, right: 0, opacity: 0.1 }}>
              <Inventory2 sx={{ fontSize: 180 }} />
            </Box>
            <Avatar
              src={imgInfo}
              alt="Info"
              sx={{
                width: 100,
                height: 100,
                mx: 'auto',
                mb: 2,
                border: '5px solid white',
                boxShadow: '0 8px 20px rgba(0,0,0,0.3)'
              }}
            />
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
              {info._cibn || 'SIN CÓDIGO'}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              NÚMERO DE INVENTARIO DEL BIEN
            </Typography>
          </Box>

          <CardContent sx={{ p: 4 }}>

            {/* SECCIÓN DE INFORMACIÓN BÁSICA */}
            <Typography variant="h6" fontWeight="bold" color="textPrimary" mb={3} gutterBottom>
              INFORMACIÓN PRINCIPAL
            </Typography>

            <Grid container spacing={3} mb={4}>
              <Grid item xs={12} sm={6}>
                <InfoItem icon={<AttachMoney />} label="COSTO" value={`$${info._csbn} MXN`} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoItem icon={<BrandingWatermark />} label="MARCA" value={info._mkbn} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoItem icon={<Memory />} label="MODELO" value={info._mdbn} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoItem icon={<QrCode />} label="NÚMERO DE SERIE" value={info._nsbn} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoItem icon={<Build />} label="ESTADO FÍSICO" value={info._efbn} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoItem
                  icon={info._iabn ? <CheckCircle/> : <Cancel/>}
                  label="ESTADO DE ASIGNACIÓN"
                  value={stateAsign(info._iabn)}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4, borderColor: 'rgba(59, 130, 246, 0.3)' }} />

            {/* SECCIÓN DE DOCUMENTACIÓN */}
            <Typography variant="h6" fontWeight="bold" color="textPrimary" mb={3} gutterBottom>
              DOCUMENTACIÓN
            </Typography>

            <Grid container spacing={3} mb={4}>
              <Grid item xs={12} sm={6}>
                <InfoItem icon={<Receipt />} label="NÚMERO DE FACTURA" value={info._nfbn} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoItem icon={<CalendarToday />} label="FECHA DE ADQUISICIÓN" value={info._fabn} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoItem icon={<Policy />} label="NÚMERO DE PÓLIZA" value={info._npbn} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoItem icon={<Tag />} label="NÚMERO DE INVENTARIO ANTERIOR" value={info._nibn} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoItem icon={<Person />} label="PROVEEDOR" value={info._pvbn} />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4, borderColor: 'rgba(59, 130, 246, 0.3)' }} />

            {/* SECCIÓN DE DESCRIPCIÓN Y UBICACIÓN */}
            <Typography variant="h6" fontWeight="bold" color="textPrimary" mb={3} gutterBottom>
              DESCRIPCIÓN Y UBICACIÓN
            </Typography>

            <Grid container spacing={3} mb={4}>
              <Grid item xs={12} sm={6}>
                <InfoItem icon={<Description />} label="DESCRIPCIÓN FÍSICA" value={info._dfbn} multiline />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoItem icon={<Description />} label="OBSERVACIONES" value={info._obbn} multiline />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoItem icon={<LocationOn />} label="ÁREA" value={info.AREA} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoItem icon={<Business />} label="EDIFICIO" value={info.EDIFICIO} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoItem icon={<Category />} label="PARTIDA" value={info.PARTIDA} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoItem icon={<Inventory2 />} label="TIPO DE BIEN" value={info.BIEN} />
              </Grid>
            </Grid>

            {/* FOTO DEL BIEN */}
            <Box sx={{ textAlign: 'center', mt: 5 }}>
              <Typography variant="h6" fontWeight="bold" color="textPrimary" mb={2}>
                FOTOGRAFÍA DEL BIEN
              </Typography>
              <Box
                sx={{
                  width: '100%',
                  maxWidth: 400,
                  height: 250,
                  mx: 'auto',
                  border: '3px dashed',
                  borderColor: '#6e3333',
                  borderRadius: 3,
                  overflow: 'hidden',
                  background: 'rgba(246, 59, 59, 0.05)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb:3,
                  boxShadow: '0 8px 25px rgba(59, 130, 246, 0.2)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 30px rgba(59, 130, 246, 0.3)'
                  }
                }}
              >
                <img
                  src={info._ftbn || noImage}
                  alt="FOTO DEL BIEN"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain'
                  }}
                />
              </Box>
            </Box>

          </CardContent>
        </Card>
      </Zoom>
    </>
  );
}

// COMPONENTE REUTILIZABLE PARA CADA CAMPO
const InfoItem = ({ icon, label, value, multiline = false }) => {
  return (
    <Box sx={{ display: 'flex', alignItems: multiline ? 'flex-start' : 'center', gap: 2, p: 2, borderRadius: 2, backgroundColor: 'rgba(246, 59, 59, 0.05)', transition: '0.3s', '&:hover': { backgroundColor: 'rgba(246, 59, 59, 0.1)' } }}>
      <Avatar sx={{ background: 'linear-gradient(180deg, #833c3cff 20%, #3b1e1eff 100%)', width: 40, height: 40, boxShadow: '0 4px 10px rgba(246, 59, 59, 0.3)' }}>
        {icon}
      </Avatar>
      <Box>
        <Typography variant="caption" color="text.secondary" fontWeight="medium">
          {label}
        </Typography>
        <Typography 
          component="div"
          fontWeight="bold" 
          sx={{ wordBreak: 'break-word', mt: multiline ? 0.5 : 0 }}
        >
          {value || '—'}
        </Typography>
      </Box>
    </Box>
  );
}